import React, { useState, useEffect, FormEvent } from 'react';
import api from '../../api/axiosConfig';

interface AdminAccount {
  _id: string;
  name: string;
  email: string;
  createdAt: string;
}

const QLTaiKhoan = () => {
  const [admins, setAdmins] = useState<AdminAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Form state
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- Lấy token từ sessionStorage ---
  const getToken = () => JSON.parse(sessionStorage.getItem('user') || '{}')?.accessToken;

  // --- Lấy danh sách tài khoản admin ---
  const fetchAdmins = async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      const { data } = await api.get('/users', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdmins(data);
    } catch (error) {
      alert('Không thể tải danh sách tài khoản. Bạn cần có quyền Admin để xem mục này.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  // --- Tạo tài khoản mới ---
  const handleCreateAdmin = async (e: FormEvent) => {
    e.preventDefault();
    if (!newName || !newEmail || !newPassword) {
      alert('Vui lòng điền đầy đủ thông tin.');
      return;
    }
    setIsSubmitting(true);
    try {
      const token = getToken();
      await api.post(
        '/users',
        { name: newName, email: newEmail, password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('✅ Tạo tài khoản admin mới thành công!');
      setNewName('');
      setNewEmail('');
      setNewPassword('');
      fetchAdmins();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Tạo tài khoản thất bại.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Xóa tài khoản admin ---
  const handleDeleteAdmin = async (adminId: string, adminName: string) => {
    if (!window.confirm(`Bạn có chắc chắn muốn xóa tài khoản "${adminName}"?`)) return;
    try {
      const token = getToken();
      await api.delete(`/users/${adminId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert('✅ Xóa tài khoản thành công!');
      fetchAdmins();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Xóa tài khoản thất bại.');
    }
  };

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Quản lý Tài khoản Admin</h1>

      {/* Nút trở về Dashboard */}
      <div className="mb-6">
        <a
          href="/admin/dashboard"
          className="text-sm text-gray-600 hover:text-blue-600 transition-colors"
        >
          ← Trở về Dashboard
        </a>
      </div>

      {/* Form thêm tài khoản */}
      <div className="mb-8 bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-800 border-b pb-2 mb-4">
          Thêm tài khoản Admin mới
        </h2>
        <form
          onSubmit={handleCreateAdmin}
          className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end"
        >
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Họ Tên
            </label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              required
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-600"
            />
          </div>

          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              required
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-600"
            />
          </div>

          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mật khẩu
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-600"
            />
          </div>

          <div className="md:col-span-1">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-5 py-2 bg-green-700 text-white font-semibold rounded-md hover:bg-green-800 transition disabled:bg-green-400"
            >
              {isSubmitting ? 'Đang tạo...' : 'Tạo tài khoản'}
            </button>
          </div>
        </form>
      </div>

      {/* Bảng danh sách tài khoản */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-800 border-b pb-2 mb-4">
          Danh sách Tài khoản
        </h2>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-green-600 text-white">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wide">
                  Họ tên
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wide">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wide">
                  Ngày tạo
                </th>
                <th className="px-6 py-3 text-center text-sm font-semibold uppercase tracking-wide">
                  Hành động
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-6 text-center text-gray-500">
                    Đang tải danh sách tài khoản...
                  </td>
                </tr>
              ) : admins.length > 0 ? (
                admins.map((admin) => (
                  <tr key={admin._id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-3 text-gray-800">{admin.name}</td>
                    <td className="px-6 py-3 text-gray-800">{admin.email}</td>
                    <td className="px-6 py-3 text-gray-800">
                      {new Date(admin.createdAt).toLocaleDateString('vi-VN')}
                    </td>
                    <td className="px-6 py-3 text-center">
                      <button
                        onClick={() => handleDeleteAdmin(admin._id, admin.name)}
                        className="px-3 py-1 text-sm text-red-600 font-semibold hover:text-red-800"
                      >
                        Xóa
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-6 py-6 text-center text-gray-500">
                    Không có tài khoản nào.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default QLTaiKhoan;
